import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import type {
  FacilityStateRequirement,
  OperationalDocument,
  RegulatoryRequirement,
} from "@services/facilityCompliance";
import {
  CfrBody,
  CfrPart,
  FacilityGroup,
  LibraryFacilityType,
  LibraryPanel,
  LibrarySearchHit,
  RequirementCategory,
  RequirementItem,
  UsStateOption,
} from "@data/regulationLibraryCatalog";

export interface CoverageArea {
  id: string;
  title: string;
  icon: "federal" | "cfr" | "state" | "ops";
  items: string[];
}

export interface RegulationLibraryPayload {
  facilityTypes: LibraryFacilityType[];
  quickPickIds: string[];
  coverageAreas: CoverageArea[];
  states: UsStateOption[];
  facility: LibraryFacilityType | null;
  state: UsStateOption | null;
  regulatoryCategories: RequirementCategory[];
  cfrBodies: CfrBody[];
  stateRequirements: RequirementItem[];
  stateNote: string | null;
  operationalDocuments: RequirementItem[];
  accreditations: RequirementItem[];
  accreditationPrograms?: import("@services/facilityCompliance").AccreditationsResponse | null;
  sourceRecords?: {
    regulatory: Record<string, RegulatoryRequirement>;
    operational: Record<string, OperationalDocument>;
    statePrograms: Record<string, FacilityStateRequirement>;
  };
  searchHits: LibrarySearchHit[];
}

interface RawItem {
  id: string;
  title: string;
  description: string;
  tags: string[];
  authority: string;
  authority_url: string;
  keywords: string[];
  aside?: string;
}

interface RawPanel {
  kind: string;
  category_id?: string;
  body_id?: string;
}

function mapItem(item: RawItem): RequirementItem {
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    tags: item.tags,
    authority: item.authority,
    authorityUrl: item.authority_url,
    keywords: item.keywords,
    aside: item.aside || undefined,
  };
}

function mapPanel(panel: RawPanel): LibraryPanel {
  if (panel.kind === "category" && panel.category_id) {
    return { kind: "category", categoryId: panel.category_id };
  }
  if (panel.kind === "cfr-body" && panel.body_id) {
    return { kind: "cfr-body", bodyId: panel.body_id };
  }
  if (
    panel.kind === "overview" ||
    panel.kind === "federal-overview" ||
    panel.kind === "regulatory-overview" ||
    panel.kind === "cfr-overview" ||
    panel.kind === "state" ||
    panel.kind === "operational" ||
    panel.kind === "accreditation"
  ) {
    return { kind: panel.kind };
  }
  return { kind: "federal-overview" };
}

function mapFacility(raw: {
  id: string;
  name: string;
  acronym?: string;
  description: string;
  group: FacilityGroup;
}): LibraryFacilityType {
  return {
    id: raw.id,
    name: raw.name,
    acronym: raw.acronym || undefined,
    description: raw.description,
    group: raw.group,
  };
}

export async function getRegulationLibrary(options?: {
  facilityType?: string;
  state?: string;
  q?: string;
}): Promise<RegulationLibraryPayload> {
  if (options?.facilityType && options?.state) {
    const { loadDynamicLibrarySource, mapDynamicLibrary } = await import("./regulationLibraryDynamic");
    const source = await loadDynamicLibrarySource(options.facilityType, options.state);
    return mapDynamicLibrary(options.facilityType, options.state, source, options.q) as RegulationLibraryPayload;
  }
  const base = getApiOrigin();
  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const params = new URLSearchParams();
  if (options?.facilityType) params.set("facility_type", options.facilityType);
  if (options?.state) params.set("state", options.state);
  if (options?.q) params.set("q", options.q);
  const query = params.toString();
  const response = await fetch(`${base}/api/v1/regulation-library${query ? `?${query}` : ""}`, { headers });
  check401(response);
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body && typeof body.detail === "string") detail = body.detail;
    } catch {
      detail = response.statusText;
    }
    throw new Error(detail || "Failed to load the regulations library");
  }
  const data = await response.json();
  const categories = (data.regulatory_categories ?? []) as Array<{
    id: string;
    title: string;
    summary: string;
    note?: string;
    items: RawItem[];
  }>;
  const bodies = (data.cfr_bodies ?? []) as Array<{
    id: string;
    name: string;
    summary: string;
    note?: string;
    section_count?: number;
    parts: Array<{
      id: string;
      title: string;
      citation: string;
      view_all_url: string;
      groups?: FacilityGroup[];
      sections: Array<{ number: string; title: string; url: string }>;
    }>;
  }>;
  return {
    facilityTypes: (data.facility_types ?? []).map(mapFacility),
    quickPickIds: data.quick_pick_ids ?? [],
    coverageAreas: data.coverage_areas ?? [],
    states: data.states ?? [],
    facility: data.facility ? mapFacility(data.facility) : null,
    state: data.state ?? null,
    regulatoryCategories: categories.map((category) => ({
      id: category.id,
      title: category.title,
      summary: category.summary,
      note: category.note,
      items: category.items.map(mapItem),
    })),
    cfrBodies: bodies.map((body) => ({
      id: body.id,
      name: body.name,
      summary: body.summary,
      note: body.note,
      sectionCount: body.section_count,
      parts: body.parts.map((part): CfrPart => ({
        id: part.id,
        title: part.title,
        citation: part.citation,
        viewAllUrl: part.view_all_url,
        groups: part.groups ?? [],
        sections: part.sections,
      })),
    })),
    stateRequirements: (data.state_requirements ?? []).map(mapItem),
    stateNote: data.state_note ?? null,
    operationalDocuments: (data.operational_documents ?? []).map(mapItem),
    accreditations: (data.accreditations ?? []).map(mapItem),
    searchHits: (data.search_hits ?? []).map((hit: { id: string; title: string; snippet: string; group: string; panel: RawPanel }): LibrarySearchHit => ({
      id: hit.id,
      title: hit.title,
      snippet: hit.snippet,
      group: hit.group,
      panel: mapPanel(hit.panel),
    })),
  };
}
